import { treeUtil } from '@salescore/buff-common'
import { isNotNullish } from 'effect/Predicate'

import type {
  ViewConfigFilter,
  ViewConfigFilterNode,
  ViewConfigKpiWithMetric,
  ViewConfigSheet,
  ViewConfigTreeNode,
} from '../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../common.ts'
import { getMetricFromKpiFormula } from '../../getMetricFromKpiFormula.ts'
import { addDateFilter } from './addDateFilter.ts'
import { addSalescoreUserField } from './addSalescoreUserFields.ts'
import { addSalescoreUserTree } from './addSalescoreUserTree.ts'

export function updateConfigByAdditionalConfig(configRaw: ViewConfigSheet, context: CompileContext): ViewConfigSheet {
  // kpi関連の値の準備
  const kpiRelated = getKpiRelatedConfig(context)

  // additionalConfigを適用する
  const additionalLeafs: ViewConfigFilter[] = [
    ...(context.additionalConfig?.filterLeafs ?? []).map((x) => x.leaf),
    ...(context.additionalConfig?.kpiParameter?.dimensionFilterLeafs ?? []),
  ].filter(isNotNullish)
  const filterTreeWithAdditionalLeafs: ViewConfigFilterNode =
    additionalLeafs.length > 0
      ? {
          logicalOperator: 'and',
          leafs: [],
          children: [
            configRaw.filterTree,
            {
              logicalOperator: 'and' as const,
              children: [],
              leafs: additionalLeafs,
            },
          ].filter(isNotNullish),
        }
      : (configRaw.filterTree ?? { logicalOperator: 'and', children: [], leafs: [] })
  const additionalFilterTrees = [
    // ...(context.additionalConfig?.filterTrees ?? []).map((x) => x.tree),
    // kpiでシートのreferenceを行い、かつkpi側での絞り込みをするような初期構想の仕様が戻るなら、以下のような処理が必要
    // kpiRelated?.measure.type === 'preset' ? kpiRelated.measure.filterTree : undefined,
  ].filter(isNotNullish)
  const filterTreeWithAdditionalTrees: ViewConfigFilterNode =
    additionalFilterTrees.length > 0
      ? {
          logicalOperator: `and`,
          children: [filterTreeWithAdditionalLeafs, ...additionalFilterTrees],
          leafs: [],
        }
      : filterTreeWithAdditionalLeafs
  const configWithFilter: ViewConfigSheet = {
    ...configRaw,
    filterTree: filterTreeWithAdditionalTrees,
  }

  //
  // previousがあり、それがkpiのとき（＝KPIからドリルダウンしてきたとき）は、userの紐付けやdateのフィルタを行う
  //
  if (kpiRelated === undefined) {
    return configWithFilter
  }

  // 紐づくSalescoreユーザー情報をツリーとフィールドに追加する
  // フィールドへの追加は、絞り込みのためにも必要なので、ロジックを変える際は注意すること
  const withUserTree = addSalescoreUserTree(configWithFilter, kpiRelated.metric.user, context, kpiRelated.metric.date)
  const withDate = addDateFilter(withUserTree, kpiRelated.metric.date)
  const withUser = addSalescoreUserField(
    withDate,
    kpiRelated.metric.user,
    context,
    kpiRelated.previousKpiConfig.options,
  )

  return mergeKpiSheetExtraConfig(withUser, kpiRelated.previousKpiConfig)
}

function getKpiRelatedConfig(context: CompileContext) {
  const previousConfig = context.additionalConfig?.previousConfig
  const previousKpiConfig = previousConfig?.type === 'kpi' ? previousConfig : undefined
  if (previousKpiConfig === undefined) {
    return
  }
  // 読み取り面 ViewConfigKpiWithMetric は inline (sheet/measure/date/user) を型から剥がしているため、
  // user/date は formula.metric 経由で読む。metric は formula が 'ref'（= sheet かつ measure が揃う）のとき
  // のみ返るので、旧ゲート（inline sheet 単独判定）に対し「sheet あり measure なし」でのみ抑制側に倒れる正規化。
  const metric = getMetricFromKpiFormula(previousKpiConfig.formula)
  if (metric === undefined) {
    return
  }

  return {
    previousKpiConfig,
    metric,
  }
}

// sheetExtraSettingが定義されていれば合成する
// 現状はブロックのフィルターのみを想定しており、これに特化した実装にしている

function mergeKpiSheetExtraConfig(config: ViewConfigSheet, kpi: ViewConfigKpiWithMetric): ViewConfigSheet {
  const { sheetExtraSetting } = kpi
  if (sheetExtraSetting === undefined) {
    return config
  }
  const { tree } = config
  if (tree === undefined) {
    return config
  }

  // 実装の関係で、絞り込みの高度な設定（children）は許していないので、かならずleafsだけのはず
  const leafs = sheetExtraSetting.filterTree?.leafs ?? []
  if (leafs.length === 0) {
    return config
  }
  const newTree = leafs.reduce((newTree, leaf) => {
    if (leaf.type !== 'property') {
      return newTree
    }
    treeUtil.exec(newTree, (node: ViewConfigTreeNode) => {
      if (node.name !== leaf.property.nodeName) {
        return node
      }
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing,no-param-reassign -- 直したいけど大丈夫なのかわからないので保留
      node.additionalJoinOn ||= {
        logicalOperator: 'and',
        leafs: [],
        children: [],
      }
      node.additionalJoinOn.leafs.push(leaf)
      return node
    })
    return newTree
  }, tree)

  return {
    ...config,
    tree: newTree,
  }
}
